import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useOutletContext } from 'react-router-dom';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Box, IconButton, Tooltip } from '@mui/material';
import { Edit as EditIcon, HowToReg as AcceptIcon, MergeType as MergeIcon } from '@mui/icons-material';

import ConstituentForm from '../ConstituentForm/ConstituentForm';
import MatchingConstituents from '../MatchingConstituents/MatchingConstituents';

import { TABLE_THEME, USER_ROLES } from '../../../util/constants/AppConstants';
import {
  acceptConstituent,
  getConstituent,
  getMatchingConstituents,
  getMatchingCouncillorConstituents,
} from '../../../http/constituentAPI';
import { setNotify } from '../../../datastore/actions/actionActions';

import './ConstituentsTable.scss';

const ConstituentsTable = ({ data, columns, loading, handleGetConstituentsData, pending }) => {
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [isConstituentFormOpen, setIsConstituentFormOpen] = useState(false);
  const [matchingConstituentsLoading, setMatchingConstituentsLoading] = useState(false);
  const [matchingConstituents, setMatchingConstituents] = useState([]);
  const [activeConstituent, setActiveConstituent] = useState(null);

  const dispatch = useDispatch();

  const config = useSelector((state) => state.config.config);

  const { userInfo } = useOutletContext();

  const mppRoleValue = USER_ROLES.find((role) => role.short === 'MPP').value;
  const councillorRoleValue = USER_ROLES.find((role) => role.short === 'Councillor').value;

  const isMpp = userInfo && userInfo.role === mppRoleValue;
  const isCouncillor = userInfo && userInfo.role === councillorRoleValue;

  const handleAcceptConstituent = async (e, id) => {
    e.stopPropagation();

    const res = await acceptConstituent(config, userInfo.id, id);
    if (res.status === 200) {
      dispatch(
        setNotify({
          status: true,
          type: 'success',
          title: 'Success',
          message: res.data.message,
        }),
      );

      handleGetConstituentsData(userInfo.id);
    } else {
      dispatch(
        setNotify({
          status: true,
          type: 'error',
          title: 'Error',
          message: res.data.message,
        }),
      );
    }
  };

  const handleGetMatchingConstituents = async (e, id, name) => {
    e.stopPropagation();

    setMatchingConstituentsLoading(true);
    setIsMergeModalOpen(true);

    const res = isCouncillor
      ? await getMatchingCouncillorConstituents(config, userInfo.id, id)
      : await getMatchingConstituents(config, userInfo.id, id);
    if (res.status === 200) {
      setMatchingConstituents(res.data.constituents);
      setActiveConstituent({ id, name });
      setMatchingConstituentsLoading(false);
    } else {
      console.log('ERROR: Grabbing Matching Constituents');
    }
  };

  const handleEditConstituent = async (e, id) => {
    e.stopPropagation();

    setIsConstituentFormOpen(true);
    const constituent = data.find((elem) => elem.id === id);
    setActiveConstituent(constituent);
  };

  const theme = createTheme(TABLE_THEME);

  const memoColumns = useMemo(() => columns, [columns]);
  const memoData = useMemo(() => data, [data]);

  const handleCreateNew = () => {
    setIsConstituentFormOpen(true);
  };

  const table = useMaterialReactTable({
    columns: memoColumns,
    data: memoData,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableColumnResizing: true,
    enableColumnFilters: true,
    enableFacetedValues: true,
    getRowId: (originalRow) => originalRow.id,
    renderTopToolbarCustomActions: () => (
      <div className="table__toolbar-wrapper">
        {!pending && (
          <div className="table__toolbar-btn create-btn" onClick={handleCreateNew}>
            Create New +
          </div>
        )}
      </div>
    ),
    state: { isLoading: loading },
    positionToolbarAlertBanner: 'none',
    muiPaginationProps: {
      color: 'primary',
      shape: 'rounded',
      showRowsPerPage: false,
      showFirstButton: true,
      showLastButton: true,
    },
    paginationDisplayMode: 'pages',
    initialState: {
      columnOrder: ['name', 'contact_count', 'poll_label', 'ward_label', 'postal_code', 'address', 'email', 'phone'],
      pagination: { pageSize: 10, pageIndex: 0 },
      showColumnFilters: true,
    },
    muiTableBodyCellProps: {
      sx: {
        marginRight: '12px',
        color: '#343434',
        fontSize: '14px',
        fontWeight: 'normal',
        border: 'none',
        whiteSpace: 'nowrap',
        overflowX: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        marginRight: '12px',
        color: '#343434',
        fontSize: '16px',
        fontWeight: 500,
        border: 'none',
      },
    },
    muiTableHeadRowProps: {
      sx: {
        borderTop: '1px solid #D3D3D3',
        borderBottom: '1px solid #D3D3D3',
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        if (!row.original.pending) {
          window.open(`/constituent/${row.original.id}`, '_blank');
        }
      },
      sx: {
        cursor: row.original.pending ? 'default' : 'pointer',
        borderBottom: '1px solid #D3D3D3',
        backgroundColor: '#FFFFFF',
        '&:hover': {
          backgroundColor: '#F7F7F7',
        },
      },
    }),
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => {
      if (pending) {
        return (
          <Box sx={{ display: 'flex', flexWrap: 'nowrap' }}>
            <Tooltip title="Accept">
              <IconButton
                className={`icon-button ${isMpp || isCouncillor ? 'blue' : ''}`}
                onClick={(e) => {
                  handleAcceptConstituent(e, row.original.id);
                }}
              >
                <AcceptIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Merge">
              <IconButton
                className={`icon-button ${isMpp || isCouncillor ? 'blue' : ''}`}
                onClick={(e) => {
                  handleGetMatchingConstituents(e, row.original.id, row.original.name);
                }}
              >
                <MergeIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }

      return (
        <Box sx={{ display: 'flex', flexWrap: 'nowrap' }}>
          <Tooltip title="Edit">
            <IconButton
              className={`icon-button ${isMpp || isCouncillor ? 'blue' : ''}`}
              onClick={(e) => {
                handleEditConstituent(e, row.original.id);
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        </Box>
      );
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        size: 100,
        grow: false,
        muiTableHeadCellProps: {
          sx: {
            fontSize: '16px',
            fontWeight: 500,
            position: 'sticky',
            right: 0,
            zIndex: 10,
            border: 'none',
            boxShadow: '-4px 0 8px -2px rgba(0, 0, 0, 0.1) !important',
          },
        },
        muiTableBodyCellProps: {
          sx: {
            position: 'sticky',
            right: 0,
            zIndex: 10,
            border: 'none',
            boxShadow: '-4px 0 8px -2px rgba(0, 0, 0, 0.1) !important',
          },
        },
      },
    },
  });

  const handleCloseEditForm = () => {
    setIsConstituentFormOpen(false);
    setActiveConstituent(null);
  };

  const handleCloseMatchingModal = () => {
    setIsMergeModalOpen(false);
    setMatchingConstituents([]);
    setActiveConstituent(null);
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="table">
        <MaterialReactTable table={table} />
      </div>
      {isConstituentFormOpen && (
        <ConstituentForm
          isOpen={isConstituentFormOpen}
          onCloseHandler={handleCloseEditForm}
          constituentData={activeConstituent}
          handleGetConstituentData={handleGetConstituentsData}
        />
      )}
      {isMergeModalOpen && (
        <MatchingConstituents
          isOpen={isMergeModalOpen}
          onCloseHandler={handleCloseMatchingModal}
          activeConstituent={activeConstituent}
          constituents={matchingConstituents}
          constituentsLoading={matchingConstituentsLoading}
          handleGetConstituentsData={handleGetConstituentsData}
        />
      )}
    </ThemeProvider>
  );
};

export default ConstituentsTable;
